import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { TrendingUp, Building2, MapPin } from 'lucide-react';
import { loadModules } from 'esri-loader';

const SharedUnitView = () => {
  const { rentGridId, unitId } = useParams();
  const [opportunity, setOpportunity] = useState(null);
  const [rentGrid, setRentGrid] = useState(null);
  const [building, setBuilding] = useState(null);
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapToken, setMapToken] = useState(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const mapRef = useRef(null);
  const viewRef = useRef(null);

  useEffect(() => {
    fetchUnitData();
    fetchMapToken();
  }, [rentGridId, unitId]);

  useEffect(() => {
    if (rentGrid && rentGridId && unitId) {
      fetchComps();
    }
  }, [rentGrid, rentGridId, unitId]);

  useEffect(() => {
    if (mapToken && tokenExpiresAt) {
      const expiresIn = new Date(tokenExpiresAt).getTime() - Date.now();
      const refreshTime = expiresIn - 60000;

      if (refreshTime > 0) {
        const timer = setTimeout(() => {
          fetchMapToken();
        }, refreshTime);

        return () => clearTimeout(timer);
      }
    }
  }, [mapToken, tokenExpiresAt]);

  const fetchMapToken = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/share-link/map-token`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch map token');
      }

      const data = await response.json();
      if (data.status && data.token) {
        setMapToken(data.token);
        if (data.expiresAt) {
          setTokenExpiresAt(data.expiresAt);
        }
      }
    } catch (err) {
      console.error('Error fetching map token:', err);
    }
  };

  const fetchUnitData = async () => {
    try {
      setLoading(true);
      setError(null);

      const decodedUnitId = decodeURIComponent(unitId);
      const response = await fetch(
        `${
          process.env.REACT_APP_NODE_URL
        }/share-link/unit/${rentGridId}/${encodeURIComponent(decodedUnitId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const data = await response.json();
      if (data.status && data.data) {
        setOpportunity(data.data.opportunity);
        setRentGrid(data.data.rentGrid);
        setBuilding(data.data.building);
      } else {
        throw new Error(data.message || 'Failed to fetch unit data');
      }
    } catch (err) {
      console.error('Error fetching unit data:', err);
      setError(err.message || 'Failed to load unit data');
    } finally {
      setLoading(false);
    }
  };

  const fetchComps = async () => {
    try {
      if (!rentGridId || !unitId) {
        return;
      }

      const decodedUnitId = decodeURIComponent(unitId);
      const response = await fetch(
        `${
          process.env.REACT_APP_NODE_URL
        }/share-link/unit/${rentGridId}/${encodeURIComponent(
          decodedUnitId,
        )}/comps`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch comps');
      }

      const data = await response.json();
      if (data.status && data.data) {
        setComps(data.data.comps || []);
      } else {
        setComps([]);
      }
    } catch (err) {
      console.error('Error fetching comps:', err);
      setComps([]);
    }
  };

  useEffect(() => {
    if (!mapToken || !building || !building.location || !mapRef.current) {
      return;
    }

    let view;

    const initializeMap = async () => {
      try {
        const [Map, MapView, Graphic, GraphicsLayer, esriConfig] =
          await loadModules([
            'esri/Map',
            'esri/views/MapView',
            'esri/Graphic',
            'esri/layers/GraphicsLayer',
            'esri/config',
          ]);

        esriConfig.apiKey = mapToken;

        const graphicsLayer = new GraphicsLayer();

        const map = new Map({
          basemap: 'gray-vector',
          layers: [graphicsLayer],
        });

        const [lng, lat] = building.location.coordinates;

        view = new MapView({
          container: mapRef.current,
          map,
          center: [lng, lat],
          zoom: 14,
        });

        viewRef.current = view;

        const subjectGraphic = new Graphic({
          geometry: {
            type: 'point',
            longitude: lng,
            latitude: lat,
          },
          symbol: {
            type: 'simple-marker',
            style: 'circle',
            color: '#EF4444',
            size: '14px',
            outline: {
              color: '#FFFFFF',
              width: 2,
            },
          },
          attributes: {
            type: 'subject',
          },
        });

        graphicsLayer.add(subjectGraphic);

        if (comps.length > 0) {
          const compGraphics = comps
            .filter(c => c.location && c.location.lng && c.location.lat)
            .map(comp => {
              return new Graphic({
                geometry: {
                  type: 'point',
                  longitude: comp.location.lng,
                  latitude: comp.location.lat,
                },
                symbol: {
                  type: 'simple-marker',
                  style: 'circle',
                  color: '#3B82F6',
                  size: '12px',
                  outline: {
                    color: '#FFFFFF',
                    width: 2,
                  },
                },
                attributes: {
                  type: 'comp',
                  address: comp.address,
                },
              });
            });

          if (compGraphics.length > 0) {
            graphicsLayer.addMany(compGraphics);
          }
        }
      } catch (err) {
        console.error('Error initializing map:', err);
      }
    };

    initializeMap();

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, [mapToken, building, comps]);

  const getConfidenceColor = confidence => {
    switch (confidence) {
      case 'high':
        return { bg: '#E8F0F1', text: '#39585C', border: '#4FA490' };
      case 'medium':
        return { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B' };
      case 'low':
        return { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444' };
      default:
        return { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: '#F9F9F9',
        }}
      >
        <CircularProgress sx={{ color: '#39585C' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: '#F9F9F9',
          gap: '1rem',
          padding: '2rem',
        }}
      >
        <Building2 size={48} color='#6B7280' />
        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
            color: '#111827',
            textAlign: 'center',
          }}
        >
          Unable to Load Unit
        </Typography>
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: '#6B7280',
            textAlign: 'center',
            maxWidth: '400px',
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  if (!opportunity) {
    return null;
  }

  const confidenceColors = getConfidenceColor(opportunity.confidence);

  // Format building address
  const formatAddress = () => {
    if (!building?.address) return null;
    const parts = [
      building.address.street,
      building.address.city,
      building.address.state,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const buildingAddress = formatAddress();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#F9F9F9',
        padding: { xs: '1rem', md: '2rem' },
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          marginX: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            paddingBottom: '1.5rem',
            borderBottom: '2px solid #39585C',
            marginBottom: '1rem',
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '1.5rem', md: '2rem' },
              fontWeight: 700,
              color: '#39585C',
              marginBottom: '0.5rem',
            }}
          >
            Rent Optimization Report
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.875rem', md: '1rem' },
              color: '#6B7280',
              marginBottom: buildingAddress ? '0.25rem' : 0,
            }}
          >
            {opportunity.building} – Unit {opportunity.unit}
          </Typography>
          {buildingAddress && (
            <Typography
              sx={{
                fontSize: { xs: '0.875rem', md: '1rem' },
                color: '#6B7280',
              }}
            >
              {buildingAddress}
            </Typography>
          )}
        </Box>

        {/* Unit Details Section */}
        <Card
          sx={{
            padding: '1.5rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Unit Details
            </Typography>
            <Chip
              label={opportunity.confidence.toUpperCase()}
              size='small'
              sx={{
                height: '24px',
                fontSize: '0.625rem',
                backgroundColor: confidenceColors.bg,
                color: confidenceColors.text,
                border: `1px solid ${confidenceColors.border}`,
                fontWeight: 600,
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
              }}
            >
              Bedrooms: {opportunity.bed ?? 'N/A'}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
              }}
            >
              Bathrooms: {opportunity.bath ?? 'N/A'}
            </Typography>
            {opportunity.sqft && (
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                }}
              >
                Square Feet: {opportunity.sqft.toLocaleString()}
              </Typography>
            )}
          </Box>
        </Card>

        {/* Rent Comparison Section */}
        <Card
          sx={{
            padding: '1.5rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '1rem',
            }}
          >
            Rent Comparison
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: '0.5rem', sm: '1rem' },
              flexWrap: 'wrap',
              backgroundColor: '#F3F4F6',
              padding: '1rem',
              borderRadius: '0.5rem',
            }}
          >
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#6B7280',
                  marginBottom: '0.25rem',
                }}
              >
                Current Rent
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  color: '#111827',
                }}
              >
                ${opportunity.currentRent.toLocaleString()}
              </Typography>
            </Box>
            <Box
              sx={{
                paddingX: { xs: '0.5rem', sm: '0.75rem' },
                paddingY: '0.5rem',
                backgroundColor:
                  Math.max(0, opportunity.delta) > 0 ? '#E8F0F1' : '#F3F4F6',
                borderRadius: '0.5rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color:
                    Math.max(0, opportunity.delta) > 0 ? '#39585C' : '#6B7280',
                  marginBottom: '0.25rem',
                }}
              >
                Delta
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  fontWeight: 700,
                  color:
                    Math.max(0, opportunity.delta) > 0 ? '#4FA490' : '#6B7280',
                }}
              >
                {Math.max(0, opportunity.delta) > 0 ? '+' : ''}$
                {Math.max(0, opportunity.delta)} (
                {Math.max(0, opportunity.deltaPercent)}%)
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#6B7280',
                  marginBottom: '0.25rem',
                }}
              >
                Recommended
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  color:
                    opportunity.recommendedRent < opportunity.currentRent
                      ? '#6B7280'
                      : '#4FA490',
                }}
              >
                ${(() => {
                  const displayRent =
                    opportunity.recommendedRent < opportunity.currentRent
                      ? 0
                      : opportunity.recommendedRent;
                  return displayRent.toLocaleString();
                })()}
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* Market Statistics Section */}
        {rentGrid && (
          <Card
            sx={{
              padding: '1.5rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#111827',
                marginBottom: '1rem',
              }}
            >
              Market Statistics
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                  }}
                >
                  Median Rent
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {rentGrid.medianRent
                    ? `$${rentGrid.medianRent.toLocaleString()}`
                    : 'N/A'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                  }}
                >
                  25th Percentile
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {rentGrid.percentile25Rent
                    ? `$${rentGrid.percentile25Rent.toLocaleString()}`
                    : 'N/A'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                  }}
                >
                  75th Percentile
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {rentGrid.percentile75Rent
                    ? `$${rentGrid.percentile75Rent.toLocaleString()}`
                    : 'N/A'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                  }}
                >
                  Total Comparables
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {opportunity.compsTotal || comps.length || 0}
                </Typography>
              </Box>
            </Box>
          </Card>
        )}

        <Card
          sx={{
            padding: '1.5rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '1rem',
            }}
          >
            Map View
          </Typography>
          <Box
            ref={mapRef}
            sx={{
              width: '100%',
              height: '400px',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              backgroundColor: '#E5E7EB',
            }}
          />
        </Card>

        {comps.length > 0 && (
          <Card
            sx={{
              padding: '1.5rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#111827',
                marginBottom: '1rem',
              }}
            >
              Comparable Units ({comps.length})
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                backgroundColor: 'transparent',
                maxHeight: '600px',
                overflowY: 'auto',
              }}
            >
              <Table
                stickyHeader
                sx={{ minWidth: { xs: '800px', md: 'auto' } }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                        backgroundColor: '#F3F4F6',
                      }}
                    >
                      Address
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                        backgroundColor: '#F3F4F6',
                      }}
                    >
                      Bed/Bath
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                        backgroundColor: '#F3F4F6',
                      }}
                    >
                      Rent
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                        backgroundColor: '#F3F4F6',
                      }}
                    >
                      $/SF
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                        backgroundColor: '#F3F4F6',
                      }}
                    >
                      Distance
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                        backgroundColor: '#F3F4F6',
                      }}
                    >
                      Match
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comps.map(comp => (
                    <TableRow
                      key={comp.id}
                      sx={{
                        '&:hover': { backgroundColor: '#F9FAFB' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              color: '#111827',
                            }}
                          >
                            {comp.address || 'N/A'}
                          </Typography>
                          {comp.isCustom && (
                            <Chip
                              label='Custom'
                              size='small'
                              sx={{
                                height: '20px',
                                fontSize: '0.625rem',
                                backgroundColor: '#3B82F6',
                                color: '#FFFFFF',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#111827',
                          }}
                        >
                          {comp.bed ?? 'N/A'}/{comp.bath ?? 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#111827',
                            fontWeight: 500,
                          }}
                        >
                          {comp.rent ? `$${comp.rent.toLocaleString()}` : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#111827',
                          }}
                        >
                          {(() => {
                            const rent = comp.rent || comp.currentRent;
                            const sqft = comp.sqft;
                            let rentPsf = comp.rentPsf;
                            if (!rentPsf && rent && sqft && sqft > 0) {
                              rentPsf = rent / sqft;
                            }
                            return rentPsf ? `$${rentPsf.toFixed(2)}` : 'N/A';
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#111827',
                          }}
                        >
                          {comp.distance ? `${comp.distance} mi` : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#111827',
                          }}
                        >
                          {comp.similarity
                            ? `${(comp.similarity * 100).toFixed(0)}%`
                            : 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default SharedUnitView;
